import { forwardRef, useState, type ChangeEvent } from "react";

import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

import FlexWrapper, { FlexDirection } from "../containers/FlexWrapper";
import Text, { TextSize, TextVariant } from "../text/Text";
import TextShimmer from "../text/TextShimmer";
import { InputLabel } from "./Input";

export enum TextAreaSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export enum TextAreaVariant {
  TRANSPARENT = "TRANSPARENT",
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
}

const StyledTextArea = withTheme(styled.textarea<
  PropsWithTheme<{
    $size: TextAreaSize;
    $variant: TextAreaVariant;
    $width?: number | string;
    $height?: number | string;
    $fillWidth?: boolean;
    $fillHeight?: boolean;
    $error?: boolean;
    $lines: number;
    $noBorder?: boolean;
    $noPadding?: boolean;
  }>
>`
  outline: none;
  resize: none;

  min-height: ${({ $lines, $size, theme, $height, $noPadding }) => {
    if ($height) {
      return "auto";
    }
    const lineHeight = match($size)
      .with(TextAreaSize.SMALL, () => Number(theme.font.sans.height.body_sm.replace("px", "")))
      .with(TextAreaSize.MEDIUM, () => Number(theme.font.sans.height.body_md.replace("px", "")))
      .with(TextAreaSize.LARGE, () => Number(theme.font.sans.height.body_lg.replace("px", "")))
      .exhaustive();

    if ($noPadding) {
      return `${lineHeight * $lines}px`;
    }

    const padding = match($size)
      .with(TextAreaSize.SMALL, () => 12)
      .with(TextAreaSize.MEDIUM, () => 16)
      .with(TextAreaSize.LARGE, () => 20)
      .exhaustive();

    return `${lineHeight * $lines + padding}px`;
  }};

  width: ${({ $fillWidth, $width }) => {
    if ($fillWidth) return "100%";
    if ($width) return getCssSizeValue($width, "0");
    return "auto";
  }};

  height: ${({ $height, $fillHeight }) => {
    if ($fillHeight) return "100%";
    if ($height) return getCssSizeValue($height);
    return "auto";
  }};

  flex: ${({ $fillHeight }) => ($fillHeight ? "1" : "none")};

  background-color: ${({ theme, $variant }) =>
    match($variant)
      .with(TextAreaVariant.TRANSPARENT, () => "transparent")
      .with(TextAreaVariant.BASE, () => theme.color.background.base)
      .with(TextAreaVariant.PRIMARY, () => theme.color.background.primary)
      .with(TextAreaVariant.SECONDARY, () => theme.color.background.secondary)
      .with(TextAreaVariant.TERTIARY, () => theme.color.background.tertiary)
      .exhaustive()};

  border: 0.5px solid
    ${({ theme, $variant, $error, $noBorder }) => {
      if ($noBorder) {
        return "none";
      }

      if ($error) {
        return theme.color.border.error;
      }

      return match($variant)
        .with(TextAreaVariant.TRANSPARENT, () => theme.color.border.primary)
        .with(TextAreaVariant.BASE, () => theme.color.border.primary)
        .with(TextAreaVariant.PRIMARY, () => theme.color.border.primary)
        .with(TextAreaVariant.SECONDARY, () => theme.color.border.primary)
        .with(TextAreaVariant.TERTIARY, () => theme.color.border.primary)
        .exhaustive();
    }};
  border-radius: 4px;
  color: ${({ theme }) => theme.color.text.primary};

  font-size: ${({ theme, $size }) => {
    return match($size)
      .with(TextAreaSize.SMALL, () => theme.font.sans.size.body_sm)
      .with(TextAreaSize.MEDIUM, () => theme.font.sans.size.body_md)
      .with(TextAreaSize.LARGE, () => theme.font.sans.size.body_lg)
      .exhaustive();
  }};
  line-height: ${({ theme, $size }) => {
    return match($size)
      .with(TextAreaSize.SMALL, () => theme.font.sans.height.body_sm)
      .with(TextAreaSize.MEDIUM, () => theme.font.sans.height.body_md)
      .with(TextAreaSize.LARGE, () => theme.font.sans.height.body_lg)
      .exhaustive();
  }};
  font-weight: ${({ theme }) => theme.font.sans.weight.regular};
  font-family: ${({ theme }) => theme.font.sans.family};

  padding: ${({ $size, $noPadding }) => {
    if ($noPadding) {
      return "0";
    }

    return match($size)
      .with(TextAreaSize.SMALL, () => "6px")
      .with(TextAreaSize.MEDIUM, () => "8px")
      .with(TextAreaSize.LARGE, () => "10px")
      .exhaustive();
  }};

  transition: border-color 0.075s ease-in-out;

  &:hover:not(:disabled, :focus) {
    border-color: ${({ theme }) => theme.color.border.primary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.color.border.selected};
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.text.disabled};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`);

const TextAreaWrapper = styled.div<{ $fillHeight?: boolean; $fillWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $fillWidth }) => ($fillWidth ? "100%" : "auto")};
  height: ${({ $fillHeight }) => ($fillHeight ? "100%" : "auto")};
  ${({ $fillHeight }) => ($fillHeight ? "flex: 1;" : "")}
`;

const LoadingContainer = withTheme(styled.div<
  PropsWithTheme<{
    $variant: TextAreaVariant;
    $size: TextAreaSize;
    $lines: number;
    $width?: number | string;
    $height?: number | string;
    $fillWidth?: boolean;
    $fillHeight?: boolean;
    $noBorder?: boolean;
    $noPadding?: boolean;
  }>
>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  min-height: ${({ $lines, $size, theme, $height, $noPadding }) => {
    if ($height) {
      return "auto";
    }
    const lineHeight = match($size)
      .with(TextAreaSize.SMALL, () => Number(theme.font.sans.height.body_sm.replace("px", "")))
      .with(TextAreaSize.MEDIUM, () => Number(theme.font.sans.height.body_md.replace("px", "")))
      .with(TextAreaSize.LARGE, () => Number(theme.font.sans.height.body_lg.replace("px", "")))
      .exhaustive();

    if ($noPadding) {
      return `${lineHeight * $lines}px`;
    }

    const padding = match($size)
      .with(TextAreaSize.SMALL, () => 12)
      .with(TextAreaSize.MEDIUM, () => 16)
      .with(TextAreaSize.LARGE, () => 20)
      .exhaustive();

    return `${lineHeight * $lines + padding}px`;
  }};

  width: ${({ $fillWidth, $width }) => {
    if ($fillWidth) return "100%";
    if ($width) return getCssSizeValue($width, "0");
    return "auto";
  }};

  height: ${({ $height, $fillHeight }) => {
    if ($fillHeight) return "100%";
    if ($height) return getCssSizeValue($height);
    return "auto";
  }};

  flex: ${({ $fillHeight }) => ($fillHeight ? "1" : "none")};

  background-color: ${({ theme, $variant }) =>
    match($variant)
      .with(TextAreaVariant.TRANSPARENT, () => "transparent")
      .with(TextAreaVariant.BASE, () => theme.color.background.base)
      .with(TextAreaVariant.PRIMARY, () => theme.color.background.primary)
      .with(TextAreaVariant.SECONDARY, () => theme.color.background.secondary)
      .with(TextAreaVariant.TERTIARY, () => theme.color.background.tertiary)
      .exhaustive()};

  border: 0.5px solid
    ${({ theme, $variant, $noBorder }) => {
      if ($noBorder) {
        return "transparent";
      }

      return match($variant)
        .with(TextAreaVariant.TRANSPARENT, () => theme.color.border.primary)
        .with(TextAreaVariant.BASE, () => theme.color.border.primary)
        .with(TextAreaVariant.PRIMARY, () => theme.color.border.primary)
        .with(TextAreaVariant.SECONDARY, () => theme.color.border.primary)
        .with(TextAreaVariant.TERTIARY, () => theme.color.border.primary)
        .exhaustive();
    }};
  border-radius: 4px;

  padding: ${({ $size, $noPadding }) => {
    if ($noPadding) {
      return "0";
    }

    return match($size)
      .with(TextAreaSize.SMALL, () => "6px")
      .with(TextAreaSize.MEDIUM, () => "8px")
      .with(TextAreaSize.LARGE, () => "10px")
      .exhaustive();
  }};
`);

export interface TextAreaInputProps {
  value?: string;
  onChange: (value: string) => void;
  variant?: TextAreaVariant;
  size?: TextAreaSize;
  lines?: number;
  placeholder?: string;
  label?: string;
  labelTooltip?: string | React.ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  min?: number;
  max?: number;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  width?: number | string;
  height?: number | string;
  fillWidth?: boolean;
  fillHeight?: boolean;
  noBorder?: boolean;
  noPadding?: boolean;
  isLoading?: boolean;
}

interface TextAreaInputState {
  error: string | undefined;
}

const DEFAULT_STATE: TextAreaInputState = {
  error: undefined,
};

const TEXTAREA_SIZE_TO_SHIMMER_HEIGHT_MAP: Record<TextAreaSize, number> = {
  [TextAreaSize.SMALL]: 12,
  [TextAreaSize.MEDIUM]: 14,
  [TextAreaSize.LARGE]: 16,
};

const TextAreaInput = forwardRef<HTMLTextAreaElement, TextAreaInputProps>(
  (
    {
      variant = TextAreaVariant.PRIMARY,
      size = TextAreaSize.MEDIUM,
      lines = 3,
      value,
      min,
      max,
      placeholder,
      label,
      labelTooltip,
      error,
      required,
      disabled,
      autoFocus,
      onChange,
      onKeyDown,
      onBlur,
      onFocus,
      width,
      height,
      fillWidth,
      fillHeight,
      noBorder,
      noPadding,
      isLoading,
    },
    ref,
  ) => {
    const [state, setState] = useState<TextAreaInputState>(DEFAULT_STATE);

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setState({
        ...state,
        error: undefined,
      });
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (value?.length && min && value.length < min) {
        setState({
          ...state,
          error: `Must be at least ${min} characters`,
        });
      }

      if (value?.length && max && value.length > max) {
        setState({
          ...state,
          error: `Must be less than ${max} characters`,
        });
      }

      onBlur?.(e);
    };

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    };

    return (
      <TextAreaWrapper $fillHeight={fillHeight} $fillWidth={fillWidth}>
        <FlexWrapper
          direction={FlexDirection.COLUMN}
          gap={4}
          fillWidth={fillWidth}
          fillHeight={fillHeight}
        >
          {label && <InputLabel label={label} labelTooltip={labelTooltip} isRequired={required} />}
          {isLoading ? (
            <LoadingContainer
              $variant={variant}
              $size={size}
              $lines={lines}
              $width={width}
              $height={height}
              $fillWidth={fillWidth}
              $fillHeight={fillHeight}
              $noBorder={noBorder}
              $noPadding={noPadding}
            >
              <TextShimmer height={TEXTAREA_SIZE_TO_SHIMMER_HEIGHT_MAP[size]} width="100%" />
              <TextShimmer height={TEXTAREA_SIZE_TO_SHIMMER_HEIGHT_MAP[size]} width="80%" />
            </LoadingContainer>
          ) : (
            <StyledTextArea
              ref={ref}
              placeholder={placeholder}
              value={value ?? ""}
              required={required}
              disabled={disabled}
              onChange={handleChange}
              autoComplete="off"
              autoCorrect="off"
              autoFocus={autoFocus}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={onKeyDown}
              $variant={variant}
              $size={size}
              $width={width}
              $height={height}
              $fillWidth={fillWidth}
              $fillHeight={fillHeight}
              $error={!!(state.error || error)}
              $lines={lines}
              $noBorder={noBorder}
              $noPadding={noPadding}
            />
          )}
          {(state.error || error) && (
            <Text variant={TextVariant.ERROR} size={TextSize.BODY_SM}>
              {state.error || error}
            </Text>
          )}
        </FlexWrapper>
      </TextAreaWrapper>
    );
  },
);

TextAreaInput.displayName = "TextAreaInput";

export default TextAreaInput;
